import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AlertTriangleIcon,
  CarIcon,
  CheckIcon,
  LucideAngularModule,
} from 'lucide-angular';
import { Producto } from '../../../types/product.types';

type VehicleCompatibilityStatus =
  | 'compatible'
  | 'incompatible'
  | 'not-selected'
  | 'loading';

@Component({
  selector: 'allas-vehicle-compatibility',

  imports: [CommonModule, LucideAngularModule],
  template: `
    <div
      [ngClass]="
        status === 'compatible'
          ? 'bg-green-50 border-green-200'
          : status === 'incompatible'
          ? 'bg-red-50 border-red-200'
          : status === 'not-selected'
          ? 'bg-amber-50 border-amber-200'
          : 'bg-gray-50 border-gray-200'
      "
      class="border rounded-lg p-2 mb-2 flex flex-col gap-2 fs-condensed"
    >
      <div class="flex items-center space-x-3">
        <!-- Car Icon with Status -->
        <div class="relative">
          <div
            class="size-8 bg-gray-600 rounded-full flex items-center justify-center"
          >
            <lucide-icon [name]="carIcon" class="size-5 text-white" />
          </div>
          <div
            [ngClass]="
              status === 'compatible'
                ? 'bg-green-500'
                : status === 'incompatible'
                ? 'bg-red-500'
                : status === 'not-selected'
                ? 'bg-amber-500'
                : 'bg-gray-500'
            "
            class="absolute -bottom-1 -right-1 size-4 rounded-full flex items-center justify-center"
          >
            <ng-container *ngIf="status === 'compatible'; else warningIcon">
              <!-- Check icon for vehicle selected -->
              <lucide-icon [name]="checkIcon" class="size-2.5 text-white" />
            </ng-container>
            <ng-template #warningIcon>
              <!-- Warning icon for no vehicle -->
              <lucide-icon
                [name]="alertTriangleIcon"
                class="w-2.5 h-2.5 text-black"
              />
            </ng-template>
          </div>
        </div>

        <!-- Message -->
        <div class="flex-1">
          @if (status === 'compatible') {
          <ng-container *ngTemplateOutlet="compatible"></ng-container>
          } @else if (status === 'loading') {
          <ng-container *ngTemplateOutlet="loading"></ng-container>
          } @else if (status === 'incompatible') {
          <ng-container *ngTemplateOutlet="incompatible"></ng-container>
          } @else if (status === 'not-selected') {
          <ng-container *ngTemplateOutlet="noVehicle"></ng-container>
          } @else {
          <ng-container *ngTemplateOutlet="loading"></ng-container>
          }
          <ng-template #compatible>
            <div class="text-sm text-gray-700">
              <span class="font-medium text-green-600">Apto para</span>
              <span class="font-semibold text-gray-800 underline ml-1">
                {{ defaultVehicle }}
              </span>
            </div>
          </ng-template>
          <ng-template #loading>
            <div class="text-sm text-gray-700">
              <span class="font-medium text-gray-600">Verificando...</span>
            </div>
          </ng-template>

          <ng-template #incompatible>
            <div class="text-sm text-gray-700">
              <span class="font-medium text-red-600">No apto para</span>
              <span class="font-semibold text-gray-800 underline ml-1">
                {{ defaultVehicle }}
              </span>
            </div>
          </ng-template>
          <ng-template #noVehicle>
            <div class="text-sm text-gray-700">
              <span
                class="font-medium text-yellow-600 underline cursor-pointer"
              >
                Selecciona un vehículo
              </span>
              <span class="text-gray-600 ml-1">
                para verificar la compatibilidad
              </span>
            </div>
          </ng-template>
        </div>
      </div>

      <!-- Catalog Attributes -->
      @if (catalogoAttributes.length > 0) {
      <div class="flex flex-wrap gap-2">
        @for (attribute of catalogoAttributes; track $index) {
        <span
          class="rounded-lg px-3 py-1 text-[10px] transition-colors duration-200 bg-green-100 text-green-800"
        >
          <strong>{{ attribute.label }}:</strong> {{ attribute.value }}
        </span>
        }
      </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class VehicleCompatibilityComponent {
  @Input() product?: Producto;
  @Input() status!: VehicleCompatibilityStatus;
  @Input() defaultVehicle!: string;

  readonly carIcon = CarIcon;
  readonly alertTriangleIcon = AlertTriangleIcon;
  readonly checkIcon = CheckIcon;

  get catalogoAttributes() {
    return (
      this.product?.atributos.filter(
        (attribute) => attribute.is_from_api_catalogo
      ) ?? []
    );
  }
}
